import ButtonSecondary from '@components/common/ButtonSecondary';
import React, { Dispatch, SetStateAction } from 'react';
import MapMobile from '@components/common/Map/MapMobile';
import HotelDetails from '@customTypes/hotel-details';
import { ProductDetails } from '@customTypes/product-details';
import Amenity from '@customTypes/amenity';
import Image from 'next/image';
import AmenityToImageNameMap from '@constants/amenityImageMap';
import { getStaticIconsUrl } from '@utils/cloudflare';
import { showAllAmenitiesClicked } from '@events/DLP';
import { slugify } from '@helpers/string';
import MobileBookingSection from '@components/BookingSection/MobileBookingSection';
import CaptureEmailDLPInLine from '@components/CaptureEmail/CaptureEmailDLPInLine';
import PoolHours from './PoolHours';

type Props = {
  products: ProductDetails | undefined;
  hotel: HotelDetails;
  setIsAmenitiesDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setProducts: Dispatch<SetStateAction<ProductDetails | undefined>>;
  urlDate: string | undefined;
  productCategory: string | null;
  handleCategoryChange: (category: string) => void;
};

export default function ProductDetailsTab({
  products,
  hotel,
  setIsAmenitiesDrawerOpen,
  setProducts,
  urlDate,
  productCategory,
  handleCategoryChange,
}: Props) {
  const getIcon = (item: Amenity): string => {
    const amenityImage = AmenityToImageNameMap[item.name];
    return getStaticIconsUrl(amenityImage);
  };

  const scrollToBookingSection = () => {
    const smartCalendarElement = document.getElementById('smart-calendar-container');
    smartCalendarElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onShowAllAmenitiesClick = () => {
    // Event
    showAllAmenitiesClicked(hotel);

    setIsAmenitiesDrawerOpen(true);
  };

  return (
    <div>
      <MobileBookingSection
        productDetails={products}
        hotelDetails={hotel}
        hotelId={hotel.metadata.hotel_id}
        urlDate={urlDate}
        setProductDetails={setProducts}
        scrollToBookingSection={scrollToBookingSection}
        productCategory={productCategory}
        handleCategoryChange={handleCategoryChange}
      />

      <div className="border-t-8 border-b-8 border-rp-gray-divider mt-8 px-4 pt-8 pb-12">
        <p className="font-rp-gelica-light leading-26px text-22 text-rp-primary-black">Amenities</p>

        <div className="mt-4">
          <div className="flex flex-col space-y-4">
            {hotel.amenities &&
              hotel.amenities.slice(0, 4).map((amenity: Amenity) => (
                <div key={amenity.icon_text} className="flex items-center space-x-4">
                  <div className="relative h-5 w-6">
                    <Image
                      data-testid={slugify(`amenity-${amenity.description}`)}
                      src={getIcon(amenity)}
                      layout="fill"
                      objectFit="contain"
                      alt={amenity.description}
                    />
                  </div>

                  <p className="-tracking-0.26 leading-6 text-base">{amenity.description}</p>
                </div>
              ))}
          </div>

          {hotel.amenities && hotel.amenities.length > 4 && (
            <div className="mt-4">
              <ButtonSecondary onClick={onShowAllAmenitiesClick}>
                Show All Amenities
              </ButtonSecondary>
            </div>
          )}
        </div>
      </div>

      {/* Pool Hours */}
      {hotel.metadata.markdown_pool_hours_details ? <PoolHours hotel={hotel} /> : null}

      {/* Map */}
      <MapMobile address={hotel.address} />

      {/* Marketing Module - Digioh Email Capture */}
      <CaptureEmailDLPInLine />
    </div>
  );
}
